import { MagnifyingGlassIcon, PlusIcon, SparkleIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Input } from '../../../components/input/Input';
import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { ITableHeader, Table, TableSortOrder } from '../../../components/table/Table';
import { getQuotations } from '../models';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { decimateAndFormatNumber } from '../../../utils/number';
import { formatDate } from '../../../utils/date';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { LinkButton } from '../../../components/button/ButtonLink';
import { PageHeading } from 'components/PageHeading';
import { ListPage } from 'components/page-templates/ListPage';
import { useDebouncedSearchParams } from 'src/hooks/useDebouncedSearchParams';
import { QuotationDraftBanner } from '../components/QuotationDraftBanner';

type QuotationSortBy = 'quotationDate' | 'expiresAt' | 'reference';

const QUOTATIONS_HEADERS: ITableHeader[] = [
  {
    id: 'reference',
    name: 'Offerte Referentie',
    sortable: true,
  },
  {
    id: 'customer',
    name: 'Klant',
  },
  {
    id: 'quotationDate',
    name: 'Offerte Datum',
    sortable: true,
  },
  {
    id: 'expiresAt',
    name: 'Vervaldatum',
    sortable: true,
  },
  {
    id: 'total',
    name: 'Totaal',
  },
];

const QuotationsPage = () => {
  const [searchParams, setSearchParams] = useDebouncedSearchParams();
  const [searchValue, _setSearchValue] = useState(() => searchParams['search'] ?? '');
  const [sortBy, setSortBy] = useState<QuotationSortBy | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<TableSortOrder | undefined>(undefined);
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return getQuotations(
        variables.cursor,
        variables.take,
        {
          search: searchValue,
        },
        sortBy,
        sortOrder,
      );
    },
    [searchValue, sortBy, sortOrder],
  );
  const page = usePagination({
    key: 'id',
    pageSize: 50,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  const setSearchValue = useCallback(
    (newValue: string) => {
      setSearchParams({ search: newValue });
      _setSearchValue(newValue);
    },
    [_setSearchValue],
  );

  useEffect(() => {
    page.reset();
  }, [searchValue, sortBy, sortOrder]);

  const title = 'Offertes';

  return (
    <>
      <PageHeader title={title} />

      <ListPage
        heading={
          <PageHeading
            noMarginBottom
            leftSide={
              <Breadcrumb
                parentItem={{
                  name: 'Financieel',
                  to: '/internal/finance',
                }}
                currentItem={title}
              />
            }
            rightSide={
              <>
                <LinkButton to="ai-draft" color="default" iconLeft={<SparkleIcon className="button-icon" />}>
                  AI Offerte
                </LinkButton>
                <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
                  Nieuwe offerte
                </LinkButton>
              </>
            }
          />
        }
        panel
        banner={<QuotationDraftBanner />}
        filters={
          <Input
            type="text"
            placeholder="Zoek een offerte..."
            value={searchValue}
            onChange={setSearchValue}
            iconLeft={<MagnifyingGlassIcon className="input-icon" />}
          />
        }
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={QUOTATIONS_HEADERS}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={(id, order) => {
            setSortBy(id as QuotationSortBy | undefined);
            setSortOrder(order);
          }}
          mapData={(quotation) => {
            const total = quotation.lines.reduce((acc, line) => {
              return acc + line.price;
            }, 0);

            return [
              <Link to={`${quotation.id}`} className="link-text">
                {quotation.reference ?? 'DRAFT'}
              </Link>,
              quotation.customer.name,
              formatDate(quotation.quotationDate),
              formatDate(quotation.expiresAt),
              <div className="whitespace-nowrap">{`€ ${decimateAndFormatNumber(total, 2, {
                decimalSeperator: ',',
              })}`}</div>,
            ];
          }}
        />
      </ListPage>
    </>
  );
};

export default QuotationsPage;
